import React, { useState, useRef } from "react";
import { Radio, Play, Pause, Volume2, VolumeX, Music, Mic } from "lucide-react";

const STATIONS = [
  { id: "ycps-fm", name: "YCPS FM", genre: "School Radio", color: "#FF6B00" },
  { id: "study-beats", name: "Study Beats", genre: "Lo-Fi Focus", color: "#3B82F6" },
  { id: "story-time", name: "Story Time", genre: "Audiobooks", color: "#8B5CF6" },
  { id: "morning-assembly", name: "Morning Assembly", genre: "Devotion & Hymns", color: "#10B981" },
];

const btn3D = "shadow-[0_4px_0_rgba(0,0,0,0.15)] hover:shadow-[0_2px_0_rgba(0,0,0,0.15)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-150";

export const SchoolRadioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activeStation, setActiveStation] = useState(STATIONS[0]);
  const [expanded, setExpanded] = useState(false);

  const togglePlay = () => setIsPlaying(!isPlaying);

  return (
    <div className="bg-gradient-to-br from-[#002147] to-[#0a3d7a] rounded-2xl overflow-hidden shadow-xl" data-testid="school-radio-player">
      {/* Main Player */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#FF6B00] flex items-center justify-center">
            <Radio size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">School Radio</h3>
            <p className="text-white/50 text-xs">Live & On-Demand Audio</p>
          </div>
        </div>

        {/* Now Playing */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlay}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white ${btn3D}`}
                style={{ backgroundColor: activeStation.color }}
                data-testid="radio-play-btn"
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
              <div>
                <p className="text-white font-semibold text-sm">{activeStation.name}</p>
                <p className="text-white/50 text-xs">{activeStation.genre}</p>
              </div>
            </div>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="text-white/50 hover:text-white transition-colors"
              data-testid="radio-mute-btn"
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>

          {/* Visualizer bars */}
          {isPlaying && (
            <div className="flex items-end gap-1 mt-3 h-6 justify-center" data-testid="radio-visualizer">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-white/40 rounded-full"
                  style={{
                    height: `${Math.random() * 100}%`,
                    animation: `pulse ${0.5 + Math.random() * 0.5}s ease-in-out infinite alternate`,
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Station Selector */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-white/60 text-xs font-medium hover:text-white transition-colors flex items-center gap-1"
          data-testid="radio-stations-toggle"
        >
          <Music size={12} /> {expanded ? "Hide Stations" : "Browse Stations"}
        </button>

        {expanded && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {STATIONS.map((station) => (
              <button
                key={station.id}
                onClick={() => { setActiveStation(station); setIsPlaying(true); }}
                className={`p-3 rounded-xl text-left transition-all ${
                  activeStation.id === station.id
                    ? "bg-white/20 ring-1 ring-white/30"
                    : "bg-white/5 hover:bg-white/10"
                }`}
                data-testid={`station-${station.id}`}
              >
                <p className="text-white text-xs font-semibold">{station.name}</p>
                <p className="text-white/40 text-[10px]">{station.genre}</p>
              </button>
            ))}
          </div>
        )}

        <p className="text-white/30 text-[10px] mt-4 text-center">
          Radio streams are simulated for demo purposes
        </p>
      </div>
    </div>
  );
};

export default SchoolRadioPlayer;
